import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Preview";
import BlockIcon from "@mui/icons-material/Block";
import { useNavigate } from "react-router-dom";
import { useAgreementTemplates } from "services/hooks/rentals";
import PreviewDialog from "./components/PreviewDialog";
import {
	AGREEMENT_TEMPLATE_META,
	LANDLORD_PREVIEW_DEFAULTS,
	TENANT_PREVIEW_DEFAULTS,
} from "./constants";

const TemplateList = ({ templateType = "landlord" }) => {
	const navigate = useNavigate();
	const meta = AGREEMENT_TEMPLATE_META[templateType] || AGREEMENT_TEMPLATE_META.landlord;
	const {
		templates,
		isLoadingTemplates,
		templatesError,
		isSubmitting,
		isPreviewing,
		deactivateTemplate,
		previewTemplate,
		actionError,
	} = useAgreementTemplates(templateType);

	const [message, setMessage] = useState({ type: "", text: "" });
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewTemplateId, setPreviewTemplateId] = useState(null);
	const [previewResult, setPreviewResult] = useState(null);
	const [previewError, setPreviewError] = useState(null);

	const list = Array.isArray(templates) ? templates : [];
	const previewDefaults =
		templateType === "tenant" ? TENANT_PREVIEW_DEFAULTS : LANDLORD_PREVIEW_DEFAULTS;

	const showMessage = (type, text) => {
		setMessage({ type, text });
		if (text) setTimeout(() => setMessage({ type: "", text: "" }), 5000);
	};

	const handleDeactivate = async (row) => {
		if (!window.confirm(`Deactivate "${row.title}"?`)) return;
		const result = await deactivateTemplate(row.id);
		if (result.success) {
			showMessage("success", result.message || "Template deactivated.");
		} else {
			showMessage("error", result.message);
		}
	};

	const handleOpenPreview = (id) => {
		setPreviewTemplateId(id);
		setPreviewResult(null);
		setPreviewError(null);
		setPreviewOpen(true);
	};

	const handlePreview = async (payload) => {
		if (!previewTemplateId) return;
		setPreviewError(null);
		const result = await previewTemplate(previewTemplateId, payload);
		if (result.success) {
			setPreviewResult(result.data);
		} else {
			setPreviewError(result.message);
		}
	};

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
				<Box>
					<Typography variant="h4">{meta.title}</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
						{meta.description}
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => navigate(meta.newPath)}
				>
					Upload agreement
				</Button>
			</Box>

			{message.text && (
				<Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>
					{message.text}
				</Alert>
			)}
			{actionError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{actionError}
				</Alert>
			)}

			{isLoadingTemplates ? (
				<Box display="flex" justifyContent="center" p={4}>
					<CircularProgress />
				</Box>
			) : templatesError ? (
				<Alert severity="error">Failed to load agreement templates.</Alert>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell>Code</TableCell>
								<TableCell>Version</TableCell>
								<TableCell>Channel</TableCell>
								<TableCell>Active</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{list.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										No templates yet. Upload your first agreement PDF.
									</TableCell>
								</TableRow>
							) : (
								list.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>{row.title}</TableCell>
										<TableCell>
											<Typography variant="body2" fontFamily="monospace">
												{row.code}
											</Typography>
										</TableCell>
										<TableCell>{row.version}</TableCell>
										<TableCell>{row.source_channel}</TableCell>
										<TableCell>{row.is_active === false ? "No" : "Yes"}</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												title="Edit positions"
												onClick={() => navigate(meta.editPath(row.id))}
											>
												<EditIcon fontSize="small" />
											</IconButton>
											<IconButton
												size="small"
												title="Preview filled PDF"
												onClick={() => handleOpenPreview(row.id)}
											>
												<PreviewIcon fontSize="small" />
											</IconButton>
											<IconButton
												size="small"
												title="Deactivate"
												onClick={() => handleDeactivate(row)}
												disabled={isSubmitting || row.is_active === false}
											>
												<BlockIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<PreviewDialog
				open={previewOpen}
				onClose={() => setPreviewOpen(false)}
				onPreview={handlePreview}
				isPreviewing={isPreviewing}
				previewResult={previewResult}
				error={previewError}
				defaultPayload={previewDefaults}
				previewDescription={
					templateType === "tenant"
						? "Sample tenant and occupancy data is sent to the API. Resolved variables are printed on the PDF at your saved positions."
						: "Sample landlord data is sent to the API. Resolved variables are printed on the PDF at your saved positions."
				}
			/>
		</Box>
	);
};

export default TemplateList;
